import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Subscription } from "../api/models"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { FetchableState, useFetchableState } from "./fetchableState"

export interface SubscriptionState extends FetchableState<Subscription> {
    debug: string
}

const initialState: SubscriptionState = {
    data: undefined,
    lastFetched: undefined,
    currentRequestId: undefined,
    error: undefined,
    debug: "subscription state"
}

export const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState: initialState,
    reducers: {
        setSubscription(state, action) {
            state.data = action.payload
            state.lastFetched = Date.now()
        }
    },
    extraReducers(builder) {
        builder.addCase(subscriptionThunk.pending, (state, action) => {
            if (state.currentRequestId)
                return
            state.currentRequestId = action.meta.requestId
        })
        builder.addCase(subscriptionThunk.fulfilled, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
            state.lastFetched = Date.now()
            state.data = action.payload.subscription
        })
        builder.addCase(subscriptionThunk.rejected, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
        })

        // switch subscription plan
        builder.addCase(switchSubscriptionThunk.pending, (state, action) => {
            if (state.currentRequestId)
                return
            state.currentRequestId = action.meta.requestId
        })
        builder.addCase(switchSubscriptionThunk.fulfilled, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
            state.lastFetched = Date.now()
            state.data = action.payload.subscription
        })
        builder.addCase(switchSubscriptionThunk.rejected, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
        })

        // cancel subscription
        builder.addCase(cancelSubscriptionThunk.pending, (state, action) => {
            if (state.currentRequestId)
                return
            state.currentRequestId = action.meta.requestId
        })
        builder.addCase(cancelSubscriptionThunk.fulfilled, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
            state.lastFetched = Date.now()
            state.data = action.payload.subscription
        })
        builder.addCase(cancelSubscriptionThunk.rejected, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
        })

        // resume subscription
        builder.addCase(resumeSubscriptionThunk.pending, (state, action) => {
            if (state.currentRequestId)
                return
            state.currentRequestId = action.meta.requestId
        })
        builder.addCase(resumeSubscriptionThunk.fulfilled, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
            state.lastFetched = Date.now()
            state.data = action.payload.subscription
        })
        builder.addCase(resumeSubscriptionThunk.rejected, (state, action) => {
            if (state.currentRequestId !== action.meta.requestId)
                return
            state.currentRequestId = undefined
        })
        
        builder.addCase(logOut, (state: SubscriptionState) => {
            state = initialState;
        })
    },
})

// GET subscription
export const subscriptionThunk = createAsyncThunk(
    'subscription/fetchSubscription',
    async ({ accessToken }: { accessToken: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.subscription.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");
        return await Api.getSubscription({ accessToken })
        // return subscription
    }
)

// PATCH subscription (switch plan or billing period)
export const switchSubscriptionThunk = createAsyncThunk(
    'subscription/switchSubscription',
    async ({ accessToken, offeringId }: { accessToken: string, offeringId: string }) => {
        return await Api.patchSubscription({ accessToken, offeringId })
    }
)

// DELETE subscription (cancel plan)
export const cancelSubscriptionThunk = createAsyncThunk(
    'subscription/cancelSubscription',
    async ({ accessToken }: { accessToken: string }) => {
        return await Api.deleteSubscription({ accessToken })
    }
)

// PUT subscription (resume plan)
export const resumeSubscriptionThunk = createAsyncThunk(
    'subscription/resumeSubscription',
    async ({ accessToken }: { accessToken: string }) => {
        return await Api.putSubscription({ accessToken })
    }
)

const subscriptionSelector = (state: RootState) => state.subscription;

export const useSubscription = (accessToken: string) => {
    return useFetchableState<Subscription>({
        selector: subscriptionSelector,
        action: subscriptionThunk({ accessToken })
    })
}

export const { setSubscription } = subscriptionSlice.actions
export default subscriptionSlice.reducer